import { useEffect, useId, useState } from "react";
import { useQuestionAnalytics } from "../../../../hooks/useQuestionAnalytics";
import { useClientTranslations } from "../../../../lib/i18n/client";
import type { TRANSLATION_MAPPINGS } from "../../../../services/questionTranslationService";
import { getTranslatedOptions } from "../../../../services/questionTranslationService";
import type { OnboardingAnswers, Question } from "../../../../types/onboarding";
import { MultiSelectDropdown } from "../../../ui/form/MultiSelectDropdown";

interface MultiSelectQuestionProps {
	question: Question;
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | string[]) => void;
	translationMappingKey: keyof typeof TRANSLATION_MAPPINGS;
	placeholderTranslationKey: string;
	eventPrefix?: string;
	maxSelections?: number;
}

export function MultiSelectQuestion({
	question,
	answers,
	onInputChange,
	translationMappingKey,
	placeholderTranslationKey,
	eventPrefix = "option",
	maxSelections,
}: MultiSelectQuestionProps) {
	const { t, isRTL } = useClientTranslations("onboarding");
	const [selectedValues, setSelectedValues] = useState<string[]>([]);
	const { trackSelection, trackDeselection } = useQuestionAnalytics(question);
	const id = useId();

	// Get translated options
	const translatedOptions = getTranslatedOptions(translationMappingKey, t);
	const options = translatedOptions.map((option) => option.value);
	const getLabelForValue = (value: string) => {
		const option = translatedOptions.find((opt) => opt.value === value);
		return option?.label || value;
	};

	// Initialize selected values from answers
	useEffect(() => {
		const currentValue = answers[question.field];
		if (Array.isArray(currentValue)) {
			setSelectedValues(currentValue);
		} else if (currentValue && typeof currentValue === "string") {
			// Handle legacy single value
			setSelectedValues([currentValue]);
		} else {
			setSelectedValues([]);
		}
	}, [answers, question.field]);

	const handleSelectionChange = (newSelectedValues: string[]) => {
		const previousValues = selectedValues;
		setSelectedValues(newSelectedValues);
		onInputChange(question.field, newSelectedValues);

		// Track analytics for selection changes
		const added = newSelectedValues.filter(
			(value) => !previousValues.includes(value),
		);
		const removed = previousValues.filter(
			(value) => !newSelectedValues.includes(value),
		);

		// Track additions
		for (const value of added) {
			trackSelection(value, newSelectedValues, eventPrefix);
		}

		// Track removals
		for (const value of removed) {
			trackDeselection(value, newSelectedValues, eventPrefix);
		}
	};

	return (
		<div dir={isRTL ? "rtl" : "ltr"}>
			<MultiSelectDropdown
				id={id}
				options={options}
				selectedValues={selectedValues}
				onSelectionChange={handleSelectionChange}
				placeholder={t(placeholderTranslationKey)}
				getLabelForValue={getLabelForValue}
				maxSelections={maxSelections}
				isRTL={isRTL}
			/>
		</div>
	);
}
